import { type DataNeededId, Dtos, type SuretyAccountId } from "@inrev/common";
import { useQueryClient } from "@tanstack/react-query";
import { useRequest } from "../../../utils/request";
import {
	dataNeededQueryKey,
	decisionsQueryKey,
	resolvedDataNeededQueryKey,
} from "../decisioning.keys";
import { type DataNeeded } from "../types";
import { invalidateListWhenIdle, useOptimisticListMutation } from "../useOptimisticListMutation";

export type ResolveDataNeededInput = {
	id: DataNeededId;
	resolution: Dtos.Decisioning.DataNeeded.Resolve.Request;
};

/** Marks an item received or closed. Resolved items leave the open list immediately. */
export const useResolveDataNeeded = (accountId: SuretyAccountId) => {
	const { post } = useRequest();
	const queryClient = useQueryClient();

	const mutation = useOptimisticListMutation<DataNeeded, ResolveDataNeededInput, DataNeeded>({
		queryKey: dataNeededQueryKey(accountId),
		request: async ({ id, resolution }) =>
			Dtos.Decisioning.DataNeeded.Get.Response.schema.parse(
				await post<unknown>(
					`/v2/admin/surety/accounts/${accountId}/data-needed/${id}/resolve`,
					resolution,
				),
			),
		apply: (items, { id }) => items.filter((item) => item.id !== id),
		affectedIds: ({ id }) => [id],
		failureMessage: ({ resolution }) =>
			resolution.status === "received"
				? "Couldn't mark the request received."
				: "Couldn't close the request.",
		reconcile: (items, resolved) => {
			queryClient.setQueryData<DataNeeded[]>(
				resolvedDataNeededQueryKey(accountId),
				(list) => list && [resolved, ...list.filter((item) => item.id !== resolved.id)],
			);
			void invalidateListWhenIdle(queryClient, decisionsQueryKey(accountId));
			return items.filter((item) => item.id !== resolved.id);
		},
	});

	return {
		resolveDataNeeded: mutation.mutate,
		/** Resolves once the server confirms, so a confirmation can wait for it; rejects on failure. */
		resolveDataNeededAsync: mutation.mutateAsync,
	};
};
